import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import CasesProvider from './context/client-context';
import { columns as baseColumns } from './components/cases-columns';
import { DataTable } from '@/components/table';
import { DataTableToolbar } from './components/data-table-toolbar';
import { CasesTableParams } from './index';
import { get_cases } from '@/api/case';
import { Case } from './data/schema';
import { useTranslation } from 'react-i18next';
import { caseListSchema } from './data/schema';
import { Main } from '@/components/layout/main'

import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

export default function CasesReportPage() {
  const [cases, setCases] = useState<Case[]>([]);
  const [params, setParams] = useState<CasesTableParams>({
    page: 0,
    page_size: 10,
    ordering: null,
    name: null,
    case_id: null,
    types: null,
    is_archived: null,
    client: null,
    assigned_to: null,
  });
  const [pagesCnt, setPagesCnt] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);
  const [selected, setSelected] = useState<{ [id: number]: { case: Case, service_fee: string } }>(() => ({}));
  const i18n = useTranslation();

  useEffect(() => {
    setLoading(true);
    get_cases(params).then((resp) => {
      // Parse with schema to ensure correct types (especially Date fields)
      const cases_list = caseListSchema.parse(resp.results || []);
      setCases(cases_list);
      setPagesCnt(Math.ceil(resp.count / params.page_size));
      setLoading(false);
      setError(false);
    }).catch(() => {
      setError(true);
      setLoading(false);
    });
  }, [params]);

  // Selection logic
  const handleSelect = (row: Case) => {
    setSelected((prev) => {
      if (prev[row.id]) {
        const copy = { ...prev };
        delete copy[row.id];
        return copy;
      } else {
        return { ...prev, [row.id]: { case: row, service_fee: '' } };
      }
    });
  };
  const isSelected = (id: number) => !!selected[id];

  // Add selection column to columns
  const columns = [
    {
      id: 'select',
      header: '',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      cell: ({ row }: any) => (
        <input
          type="checkbox"
          checked={isSelected(row.original.id)}
          onChange={() => handleSelect(row.original)}
        />
      ),
      meta: { className: 'w-8' },
      enableSorting: false,
      enableHiding: false,
    },
    ...baseColumns,
  ];

  const exportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Cases Report');
    worksheet.columns = [
      { header: i18n.t('case_id'), key: 'id', width: 10 },
      { header: i18n.t('case_type'), key: 'case_type', width: 20 },
      { header: i18n.t('case_number_full'), key: 'case_number_full', width: 20 },
      { header: i18n.t('client_name'), key: 'client_name', width: 20 },
      { header: i18n.t('created_at'), key: 'created_at', width: 18 },
      { header: i18n.t('archived_at'), key: 'archived_at', width: 18 },
      { header: i18n.t('service_fee'), key: 'service_fee', width: 15 },
    ];
    Object.values(selected).forEach(({ case: c }) => {
      worksheet.addRow({
        id: c.id,
        case_type: c.type ? c.type.name : '',
        case_number_full: c.year ? `${c.year}/${c.code}/${c.number}` : '',
        client_name: c.client ? c.client.name : '',
        created_at: c.created_at instanceof Date ? c.created_at.toLocaleDateString() : new Date(c.created_at).toLocaleDateString(),
        archived_at: c.archived_at ? (c.archived_at instanceof Date ? c.archived_at.toLocaleDateString() : new Date(c.archived_at).toLocaleDateString()) : '',
        service_fee: c.service_fee ?? '',
      });
    });
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), 'cases_report.xlsx');
  };

  return (
    <CasesProvider>
      <Main>
        <div className="mx-auto">
          <h2 className="text-2xl font-bold mb-4">{i18n.t('select_cases_for_report')}</h2>
          {loading ? <div>{i18n.t('loading')}</div> : error ? <div>{i18n.t('table_load_error')}</div> : (
            <DataTable
              data={cases}
              columns={columns}
              params={params}
              setParams={setParams}
              pages={pagesCnt}
              ToolbarName={DataTableToolbar}
            />
          )}
          <div className="mt-8">
            <h3 className="text-xl font-semibold mb-2">{i18n.t('added_cases')}</h3>
            <div className="relative w-full overflow-hidden rounded-md border border-border bg-background">
              {Object.keys(selected).length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  {i18n.t('no_case_selected')}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{i18n.t('case_id')}</TableHead>
                      <TableHead>{i18n.t('case_type')}</TableHead>
                      <TableHead>{i18n.t('case_number_full')}</TableHead>
                      <TableHead>{i18n.t('client_name')}</TableHead>
                      <TableHead>{i18n.t('created_at')}</TableHead>
                      <TableHead>{i18n.t('archived_at')}</TableHead>
                      <TableHead>{i18n.t('service_fee')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.values(selected).map(({ case: c }) => (
                      <TableRow key={c.id}>
                        <TableCell>{c.id}</TableCell>
                        <TableCell>{c.type ? c.type.name : '-'}</TableCell>
                        <TableCell>{c.year ? `${c.year}/${c.code}/${c.number}` : ''}</TableCell>
                        <TableCell>{c.client ? c.client.name : '-'}</TableCell>
                        <TableCell>{c.created_at instanceof Date ? c.created_at.toLocaleDateString() : new Date(c.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>{c.archived_at ? (c.archived_at instanceof Date ? c.archived_at.toLocaleDateString() : new Date(c.archived_at).toLocaleDateString()) : '-'}</TableCell>
                        <TableCell>{c.service_fee ?? ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={() => setSelected({})} variant="outline">{i18n.t('unselect_all')}</Button>
            <Button onClick={exportExcel} disabled={Object.keys(selected).length === 0}>{i18n.t('export_as_excel')}</Button>
          </div>
        </div>
      </Main>
    </CasesProvider>
  );
}
